import { useMemo } from 'react';
import { useAuthStore } from '@/stores/authStore';
import type { components } from '@/types/api.generated';

// Import generated types from OpenAPI spec
type AttendanceSettings = components['schemas']['AttendanceSettings'];
type ParentPortalSettings = components['schemas']['ParentPortalSettings'];

type ModuleName = 'attendance' | 'parentPortal' | 'sms' | 'messaging' | 'schoolwideSms';

interface UseFeatureModuleResult<T> {
  isEnabled: boolean;
  settings: T | null;
  isLoading: boolean;
  isAllowed: boolean;
}

/**
 * Hook to check if a feature module is enabled
 * Returns module settings if enabled, null if disabled
 *
 * @example
 * const { isEnabled, settings } = useFeatureModule('attendance');
 * if (!isEnabled) return <div>Attendance not enabled</div>;
 * // settings.defaultStatus, settings.retroactiveDays, etc.
 */
export function useFeatureModule(module: 'attendance'): UseFeatureModuleResult<AttendanceSettings>;
// eslint-disable-next-line no-redeclare
export function useFeatureModule(module: 'parentPortal'): UseFeatureModuleResult<ParentPortalSettings>;
// eslint-disable-next-line no-redeclare
export function useFeatureModule(module: 'sms'): UseFeatureModuleResult<{ enabled: boolean }>;
// eslint-disable-next-line no-redeclare
export function useFeatureModule(module: 'messaging'): UseFeatureModuleResult<{ enabled: boolean }>;
// eslint-disable-next-line no-redeclare
export function useFeatureModule(module: 'schoolwideSms'): UseFeatureModuleResult<{ enabled: boolean }>;
// eslint-disable-next-line no-redeclare, @typescript-eslint/no-explicit-any
export function useFeatureModule(module: ModuleName): UseFeatureModuleResult<any> {
  const { tenant, isLoading } = useAuthStore();

  const result = useMemo(() => {
    if (!tenant?.settings) {
      return { isEnabled: false, settings: null, isAllowed: false };
    }

    // Check if module is allowed by super admin (from master DB)
    const isAllowed = tenant.allowedModules?.[module] ?? false;

    // Special handling for attendance - uses typed columns at root level, not JSONB
    // This aligns with the database architecture where attendance fields are typed columns
    if (module === 'attendance') {
      const tenantEnabled = tenant.settings.attendanceEnabled ?? false;

      // CRITICAL: Two-layer permission check prevents tenants from accessing premium modules
      // without super admin authorization. Both conditions must be true:
      // 1. isAllowed (super admin granted access in master DB)
      // 2. tenantEnabled (tenant admin enabled in school config)
      // If this logic is modified, tenants could bypass payment/authorization!
      const isEnabled = isAllowed && tenantEnabled;
      return {
        isEnabled,
        isAllowed,
        settings: isEnabled
          ? {
              enabled: true,
              defaultStatus: tenant.settings.modules?.attendance?.defaultStatus ?? 'present',
              allowRetroactive: tenant.settings.attendanceAllowRetroactive ?? true,
              retroactiveDays: tenant.settings.attendanceRetroactiveDays ?? 7,
              requireNotes: tenant.settings.attendanceRequireNotes ?? false,
            }
          : null,
      };
    }

    // Special handling for messaging - uses typed column at root level, not JSONB
    if (module === 'messaging') {
      const tenantEnabled = tenant.settings.messagesEnabled ?? false;
      const isEnabled = isAllowed && tenantEnabled;
      return {
        isEnabled,
        isAllowed,
        settings: isEnabled ? { enabled: true } : null,
      };
    }

    // Special handling for sms - uses typed column at root level (Layer 2 pause for all non-emergency SMS)
    if (module === 'sms') {
      const tenantEnabled = tenant.settings.smsEnabled ?? false;
      const isEnabled = isAllowed && tenantEnabled;
      return {
        isEnabled,
        isAllowed,
        settings: isEnabled ? { enabled: true } : null,
      };
    }

    // Special handling for schoolwideSms - uses typed column at root level, not JSONB
    if (module === 'schoolwideSms') {
      const tenantEnabled = tenant.settings.schoolwideSmsEnabled ?? false;
      const isEnabled = isAllowed && tenantEnabled;
      return {
        isEnabled,
        isAllowed,
        settings: isEnabled ? { enabled: true } : null,
      };
    }

    // parentPortal uses the JSONB modules structure
    const moduleSettings = tenant.settings.modules?.[module as 'parentPortal'];
    const tenantEnabled = moduleSettings?.enabled ?? false;

    // CRITICAL: Two-layer permission check prevents tenants from accessing premium modules
    // without super admin authorization. Both conditions must be true:
    // 1. isAllowed (super admin granted access in master DB)
    // 2. tenantEnabled (tenant admin enabled in school config)
    // If this logic is modified, tenants could bypass payment/authorization!
    const isEnabled = isAllowed && tenantEnabled;

    return {
      isEnabled,
      isAllowed,
      settings: isEnabled ? moduleSettings : null,
    };
  }, [tenant, module]);

  return {
    ...result,
    isLoading,
  };
}
